import { Export } from "@dto/pageResponse.dto";
import { PlainReport } from "@dto/report.dto";
import { Dropdown, DropdownProps, } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { AxiosRequestConfig } from "axios";
import { FC, useState } from "react";
import { IconType } from "react-icons";
import { BsFiletypeCsv, BsFiletypeJson, BsFiletypePdf, BsFiletypeXlsx } from "react-icons/bs";
import { FaDownload } from "react-icons/fa";
import { XmApiDownloader } from "src/component/apiDownloader/XmApiDownloader";
import { XmButton } from "src/component/button/XmButton";
import { useAppGlobStore } from "../../appGlobalStore";
import { DateUtil } from "../../common/util/DateUtil";
import './ApiDownloader.less';

const EXPORT_TYPES = { PDF: BsFiletypePdf, CSV: BsFiletypeCsv, XLSX: BsFiletypeXlsx, JSON: BsFiletypeJson } as const;

type ExportTypes = {
  [key in keyof typeof EXPORT_TYPES]?: boolean;
};

export interface XmExportIconProps {
  export: Export;
  exportableTypes?: ExportTypes;
  size?: 'large' | "medium" | 'normal' | "small";
}

const allOptions: ExportTypes = {};
Object.keys(EXPORT_TYPES).forEach(type => { allOptions[type] = true })

const XmExportIcon: FC<XmExportIconProps> = ({ export: { method, url, body, fileName, type }, exportableTypes: exportAbleTypes = allOptions, size = 'normal' }) => {
  const [downloadCfg, setDownloadCfg] = useState<AxiosRequestConfig>();
  const [items] = useState<ItemType[]>(() => {
    const items: ItemType[] = [];
    Object.keys(EXPORT_TYPES).forEach(type => {
      if (exportAbleTypes[type] || exportAbleTypes[type] === undefined)
        items.push({ key: type, label: <a>{type.toUpperCase()}</a> })
    });

    return items;
  });

  if (!items.length)
    return null;

  const download = (format: string) => {
    const data = body;
    if (type === 'update_time') {
      const interval = useAppGlobStore.getState().syncBoxCtx?.interval;
      let date_from: Date;
      let date_to: Date;
      if (interval === 'custom') {
        date_from = new Date(useAppGlobStore.getState().syncBoxCtx?.start * 1000);
        date_to = new Date(useAppGlobStore.getState().syncBoxCtx?.end * 1000);
      } else {
        const startEnd = DateUtil.getRange(interval);
        date_from = new Date(startEnd.start * 1000);
        date_to = new Date(startEnd.end * 1000);
      }
      (data as PlainReport).time_range = { date_from, date_to };
    }
    setDownloadCfg({ method, url, data, params: { format } });
  }

  if (items.length == 1) {
    const key = items[0].key as string;
    const handleClick = () => download(key.toLowerCase());
    const Icon: IconType = EXPORT_TYPES[key];
    const muted = Icon === EXPORT_TYPES.PDF ? ' xm-icon-muted' : '';

    // a button, not a bare icon: export is read-only, so it stays enabled for readonly users
    return <>
      <XmButton type="text" ignoreReadonly className="export" title={"Export as " + key} data-testid="export-table" onClick={handleClick}
        icon={<Icon className={`xm-icon xm-icon-file xm-icon-file-${key} xm-icon-${size}${muted}`} />} />
      <XmApiDownloader fileName={`${fileName}.${key}`} request={downloadCfg} />
    </>;
  }

  const handleClick: DropdownProps['menu']['onClick'] = info => download(info.key.toString().toLowerCase());

  return (
    <a className="export" data-testid="export-table">
      <Dropdown
        menu={{
          className: "xm-export-options",
          items,
          onClick: handleClick
        }}
        placement="bottomRight"
      >
        <span><FaDownload className={`xm-icon xm-icon-${size}`} /></span>
      </Dropdown>
      <XmApiDownloader fileName={`${fileName}.${downloadCfg?.params.format}`} request={downloadCfg} />
    </a>
  );
};

export default XmExportIcon;